import { Alert, Badge, Button } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import type { Coverage } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { useCallback, useEffect, useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'
import {
  buildInsuranceOrgDecisionQuestionnaireResponse,
  fetchLatestInsuranceOrgDecision,
  resolveCurrentOrganizationId,
  type InsuranceOrgDecision,
} from '@/utils/insuranceQuestionnaire'
import {
  EligibilityDecisionModal,
  type EligibilityDecisionMode,
} from '@/components/ui/EligibilityDecisionModal'
import { formatDateTime } from '@/utils'
import { useInvalidateInsuranceVerdicts } from '@/hooks/useInsuranceVerdicts'

type Props = {
  patientId: string
  coverage: Coverage | null
  encounterId?: string
  organizationId?: string | null
  onSaved?: () => void
}

export function InsuranceOrgDecisionPanel({
  patientId,
  coverage,
  encounterId,
  organizationId,
  onSaved,
}: Props) {
  const medplum = useMedplum()
  const invalidateInsuranceVerdicts = useInvalidateInsuranceVerdicts()
  const [decision, setDecision] = useState<InsuranceOrgDecision | null>(null)
  const [resolvedOrganizationId, setResolvedOrganizationId] = useState<string | null>(
    organizationId || null,
  )
  const [mode, setMode] = useState<EligibilityDecisionMode | null>(null)
  const [loading, setLoading] = useState(false)

  const loadDecision = useCallback(async () => {
    if (!coverage?.id) {
      setDecision(null)
      return
    }

    const nextOrganizationId =
      organizationId || (await resolveCurrentOrganizationId(medplum, medplum.getProfile()?.id))
    setResolvedOrganizationId(nextOrganizationId)

    if (!nextOrganizationId) {
      setDecision(null)
      return
    }

    const nextDecision = await fetchLatestInsuranceOrgDecision(medplum, {
      patientId,
      coverageId: coverage.id,
      organizationId: nextOrganizationId,
    })
    setDecision(nextDecision)
  }, [coverage?.id, medplum, organizationId, patientId])

  useEffect(() => {
    void loadDecision()
  }, [loadDecision])

  const handleSubmit = async (values: { reason: string; notes: string }) => {
    if (!mode || !coverage?.id) {
      return
    }

    const profile = medplum.getProfile()
    if (!profile || profile.resourceType !== 'Practitioner') {
      showNotification({ color: 'red', message: 'A practitioner profile is required.' })
      return
    }

    const effectiveOrganizationId =
      resolvedOrganizationId || (await resolveCurrentOrganizationId(medplum, profile.id))

    if (!effectiveOrganizationId) {
      showNotification({ color: 'red', message: 'Unable to resolve the current organization.' })
      return
    }

    setLoading(true)
    try {
      await medplum.createResource(
        buildInsuranceOrgDecisionQuestionnaireResponse({
          patientId,
          practitioner: profile,
          organizationId: effectiveOrganizationId,
          coverage,
          mode,
          reason: values.reason,
          notes: values.notes,
          encounterId,
        }),
      )

      setMode(null)
      await loadDecision()
      // The decision feeds the insurance verdict every console surface shows.
      void invalidateInsuranceVerdicts()
      onSaved?.()
      showNotification({
        color: 'green',
        message:
          mode === 'approve'
            ? 'Insurance approved for this clinic.'
            : 'Insurance declined for this clinic.',
      })
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Unable to save insurance decision.',
      })
    } finally {
      setLoading(false)
    }
  }

  const badgeColor =
    decision?.mode === 'approve' ? 'green' : decision?.mode === 'disapprove' ? 'red' : 'gray'
  const badgeLabel =
    decision?.mode === 'approve'
      ? 'Accepted by Clinic'
      : decision?.mode === 'disapprove'
        ? 'Not Accepted by Clinic'
        : 'No Decision'

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <EligibilityDecisionModal
        mode={mode ?? 'approve'}
        opened={Boolean(mode)}
        onClose={() => setMode(null)}
        onSubmit={handleSubmit}
        loading={loading}
        context="organization"
      />
      <div className="mb-2 flex items-center justify-start gap-3">
        <div className="text-sm font-semibold text-gray-900">Decision</div>
        <Badge color={badgeColor} variant="light">
          {badgeLabel}
        </Badge>
      </div>
      {decision ? (
        <div className="space-y-1 text-sm text-gray-700">
          <div>
            <b>Reason:</b> {decision.reason || '-'}
          </div>
          <div>
            <b>Reviewed by:</b> {decision.reviewedBy || '-'}
          </div>
          <div>
            <b>Reviewed on:</b>{' '}
            {decision.reviewedOn ? formatDateTime(decision.reviewedOn, '-', true, true) : '-'}
          </div>
          <div>
            <b>Notes:</b>
            <div className="whitespace-pre-line">{decision.notes || '-'}</div>
          </div>
        </div>
      ) : (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          No clinic-level acceptance decision has been recorded for this insurance.
        </Alert>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          size="xs"
          variant="outline"
          color="green"
          disabled={!coverage || decision?.mode === 'approve'}
          onClick={() => setMode('approve')}
        >
          Approve
        </Button>
        <Button
          size="xs"
          variant="outline"
          color="red"
          disabled={!coverage || decision?.mode === 'disapprove'}
          onClick={() => setMode('disapprove')}
        >
          Disapprove
        </Button>
      </div>
    </div>
  )
}
